import { useState, useEffect, useContext } from "react";
import { formatDistanceToNow, differenceInMinutes, parseISO, parse, addHours, isEqual, isBefore, isAfter, format } from "date-fns";
import {
  UserCircleIcon,
  CheckBadgeIcon,
  XCircleIcon,
  ClipboardDocumentCheckIcon,
  HandThumbUpIcon,
  ArrowRightCircleIcon,
} from "@heroicons/react/20/solid";
import { AuthContext } from "../AuthContext";
import { useWebSocket } from "../WebSocketContext";
import { ChatBubbleBottomCenterIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import IndividualActions from "./individualactions";
import WorkCoachToday from "./workcoachtoday";

export default function ActivityFeed({ mobileView = false }) {

  const Navigate = useNavigate();
  
  const [combinedFeed, setCombinedFeed] = useState([]); // Merged feed of activity and social data
  const user = useContext(AuthContext); // Get the user object from AuthContext
  const { sendMessage, readyState } = useWebSocket();


  const [reply, setReply] = useState(null); // Message to reply to
  const [currentTask, setCurrentTask] = useState(null);

  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.detail;
      if (!data || data.type === "ping") return;

      if (data.success) {
        if (data.type === "activityLog") {
          processActivityLog(data);
        } else if (data.type === "activityUpdate") {
          processActivityUpdate(data);
        }
      }
    };

    window.addEventListener("insightos-websocket-message", handleMessage);

    // Request fresh activity data whenever the shared /ws connection is ready
    if (readyState === WebSocket.OPEN) {
      sendMessage({ type: "requestActivityLog" });
    }

    return () => {
      window.removeEventListener("insightos-websocket-message", handleMessage);
    };
  }, [user.access, sendMessage, readyState]);

    const getItemKey = (item) => {
      if (!item) return null;
      return (
        item.id ||
        item.ID ||
        item.PostId ||
        item.PostID ||
        item.MessageId ||
        item.messageId ||
        item.msgId ||
        (item.StaffId && item.timestamp ? `${item.StaffId}-${item.timestamp}` : null) ||
        JSON.stringify([item.type, item.Content || item.Content || item.Content])
      );
    };

    const processActivityLog = (data) => {
      let formattedData = [];

      // Only include clockins for managers/admins
      if (user.access !== 'staff') {
        formattedData = data.clockins.map(item => ({
          ...item,
          type: "activity",
          timestamp: item.updatedAt,
        }));
      }

      // Process posts, comments, likes based on user access
      const posts = processPostsData(data.posts);
      const comments = processCommentsData(data.comments);
      const likes = processLikesData(data.likes);

      // Add messages and other data types
      formattedData = [
        ...formattedData,
        ...data.messages.map(item => ({
          ...item,
          type: "message",
          timestamp: item.createdAt,
        })),
        ...posts,
        ...likes,
        ...comments,
      ];

      // Deduplicate by stable key (prefer server ids over timestamps)
      const byKey = new Map();
      formattedData.forEach(item => {
        const k = getItemKey(item) || `${item.type}-${item.timestamp}`;
        const existing = byKey.get(k);
        if (!existing) {
          byKey.set(k, item);
        } else {
          // keep the newest timestamped item
          const existingTs = existing.timestamp ? new Date(existing.timestamp).getTime() : 0;
          const incomingTs = item.timestamp ? new Date(item.timestamp).getTime() : 0;
          if (incomingTs > existingTs) byKey.set(k, item);
        }
      });

      const deduped = Array.from(byKey.values())
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 50);

      setCombinedFeed(deduped);
    };

    const processActivityUpdate = (data) => {
      // Skip clockin updates for staff users
      if (data.updateType === "activity" && user.access === 'staff') {
        return;
      }

      let newEntry = null;

      switch (data.updateType) {
        case "like":
          if (canViewLike(data.likes[0])) {
            newEntry = { ...data.likes[0], type: "like", timestamp: data.timestamp };
          }
          break;
        case "comment":
          if (canViewComment(data.comments[0])) {
            newEntry = { ...data.comments[0], type: "comment", timestamp: data.timestamp };
          }
          break;
        case "post":
          if (canViewPost(data.posts[0])) {
            newEntry = { ...data.posts[0], type: "post", timestamp: data.timestamp };
          }
          break;
      }

      if (newEntry) {
        setCombinedFeed(prev => {
          const merged = [newEntry, ...prev];
          const byKey = new Map();
          for (const item of merged) {
            const k = getItemKey(item) || `${item.type}-${item.timestamp}`;
            const existing = byKey.get(k);
            if (!existing) byKey.set(k, item);
            else {
              const existingTs = existing.timestamp ? new Date(existing.timestamp).getTime() : 0;
              const incomingTs = item.timestamp ? new Date(item.timestamp).getTime() : 0;
              if (incomingTs > existingTs) byKey.set(k, item);
            }
          }

          return Array.from(byKey.values())
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .slice(0, 50);
        });
      }
    };

  // Helper functions to determine visibility based on user role
  const canViewPost = (post) => {
    if (!post) return false;
    return user.access !== "staff" || !(post.Tags && post.Tags === "Managers");
  };

  const canViewComment = (comment) => {
    if (!comment) return false;
    return user.access !== "staff" || comment.Manager !== 1;
  };

  const canViewLike = (like) => {
    if (!like) return false;
    return user.access !== "staff" || like.Manager !== 1;
  };

  // Process data based on user access level
  const processPostsData = (posts) => {
    if (!posts || !Array.isArray(posts)) return [];
    
    if (user.access === "staff") {
      return posts
        .filter(item => !(item.Tags && item.Tags === "Managers"))
        .map(item => ({
          ...item,
          type: "post",
          timestamp: item.CreatedAt,
        }));
    }
    
    return posts.map(item => ({
      ...item,
      type: "post",
      timestamp: item.CreatedAt,
    }));
  };

  const processCommentsData = (comments) => {
    if (!comments || !Array.isArray(comments)) return [];
    
    if (user.access === "staff") {
      return comments
        .filter(item => item.Manager !== 1)
        .map(item => ({
          ...item,
          type: "comment",
          timestamp: item.CreatedAt,
        }));
    }
    
    return comments.map(item => ({
      ...item,
      type: "comment",
      timestamp: item.CreatedAt,
    }));
  };

  const processLikesData = (likes) => {
    if (!likes || !Array.isArray(likes)) return [];
    
    if (user.access === "staff") {
      return likes
        .filter(item => item.Manager !== 1)
        .map(item => ({
          ...item,
          type: "like",
          timestamp: item.LikedAt,
        }));
    }
    
    return likes.map(item => ({
      ...item,
      type: "like",
      timestamp: item.LikedAt,
    }));
  };


  useEffect(() => {
    const fetchTasks = async () => {
      try {
      const response = await fetch("https://api.insightos.co.uk/api/mydailytasks", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch daily tasks");

      const data = await response.json();
      const sortedTasks = data.data.sort((a, b) =>
        parse(a.StartTime, "HH:mm:ss", new Date()) - parse(b.StartTime, "HH:mm:ss", new Date())
      );

      const now = new Date();
      const current = sortedTasks.find((task) => {
        const taskStartTime = parse(task.StartTime, "HH:mm:ss", new Date());
        const taskEndTime = addHours(taskStartTime, 1);
        return isEqual(taskStartTime, now) || (isBefore(taskStartTime, now) && isAfter(taskEndTime, now));
      });

      if (current) setCurrentTask(current);
      } catch (error) {
      console.error("Failed to fetch tasks:", error);
      }
    };

    fetchTasks();
    // Update every 10 minutes (600,000 milliseconds) instead of every 6 seconds
    const intervalId = setInterval(fetchTasks, 600000);
    return () => clearInterval(intervalId);
    }, []);

  // Helper to format time strings (attempt ISO parse, fallback to raw)
  const formatTimeShort = (timeStr) => {
    if (!timeStr) return '';
    try {
      // Try parsing full datetime first
      const parsedISO = parseISO(timeStr);
      if (!isNaN(parsedISO)) return format(parsedISO, 'HH:mm');
    } catch (e) {
      // fallthrough
    }
    try {
      // Try parsing as time-only HH:mm:ss
      const parsed = parse(timeStr, 'HH:mm:ss', new Date());
      if (!isNaN(parsed)) return format(parsed, 'HH:mm');
    } catch (e) {
      // fallthrough
    }
    // Fallback: return as-is
    return timeStr;
  };


  const handleLike = async (item) => {
    // Using credentials: 'include' for authentication instead of token
    
    const newLike = {
      PostId: item.PostId,
      StaffId: user.id,
      StaffEmail: user.email,
      StaffName: user.name
    };
    console.log("newLike", newLike);

    try {
      const response = await fetch(`https://api.insightos.co.uk/api/likes/${item.PostId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newLike),
      });

      if (!response.ok) {
        throw new Error('Failed to like post');
      }

      const data = await response.json();
      console.log(data);

    } catch (error) {
      console.error(error);
    }
  };











  





  if (reply) {
    return (
      Navigate('/chatdashboard', { state: { reply: reply } })
    )
  }


  return (
    <div className={`p-4 max-w-4xl mx-auto ${mobileView ? 'mobile-activity-feed' : ''}`}>
      <div className="relative overflow-hidden mb-4 rounded-2xl border border-white/35 bg-gradient-to-br from-sky-500/25 via-indigo-500/20 to-emerald-500/20 backdrop-blur-xl px-4 py-3 shadow-[0_14px_35px_rgba(15,23,42,0.35),inset_0_1px_0_rgba(255,255,255,0.35)]">
        <div className="pointer-events-none absolute -top-8 -left-8 h-24 w-24 rounded-full bg-white/25 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-10 -right-8 h-24 w-24 rounded-full bg-sky-300/20 blur-2xl" />
        <div className="relative z-10 flex flex-col justify-between items-start">
          <p className="text-sm font-semibold text-indigo-900/90 mt-1">{user.name}</p>
          <p className="text-sm font-semibold text-indigo-900/80">Manager: {user.manager}</p>
        </div>
      </div>
    {currentTask && (
  <div
    className={`group relative flex flex-col space-y-3 p-4 mb-3 rounded-2xl border border-white/35 bg-gradient-to-br from-indigo-100/60 via-sky-100/55 to-emerald-100/50 backdrop-blur-xl shadow-[0_14px_35px_rgba(15,23,42,0.25),inset_0_1px_0_rgba(255,255,255,0.35)] transition-transform duration-300 transform hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(15,23,42,0.35),inset_0_1px_0_rgba(255,255,255,0.35)] mt-2 ${
      differenceInMinutes(
        addHours(parse(currentTask.StartTime, "HH:mm:ss", new Date()), 1),
        new Date()
      ) <= 1
        ? "animate-pulse"
        : ""
    }`}
  >
   {currentTask.Break === 0 && (
        <div className="flex justify-end space-x-2">
          <button className="text-indigo-900 hover:scale-105" onClick={() => Navigate('/team-tasks')}>
            <ArrowRightCircleIcon className="h-6 w-6 text-violet-700" />
          </button>
          <button className="text-indigo-900 hover:scale-105" onClick={() => Navigate('/chatdashboard')}>
            <ChatBubbleBottomCenterIcon className="h-6 w-6 text-violet-700" />
          </button>
        </div>
      )}
        

        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2">
              <span className="text-xl">🎯</span>
              <span className="leading-tight">{currentTask.Task}</span>
            </h3>
            <p className="text-sm text-indigo-700 mt-1">{currentTask.Description}</p>
          </div>

          {/* Badges */}
          <div className="flex flex-col items-end space-y-2">
            <div className="inline-flex items-center gap-2 px-2 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700">
              <span>⏰</span>
              <span>{currentTask.StartTime}</span>
            </div>
            <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-semibold ${currentTask.Priority === 'High' ? 'bg-indigo-100 text-indigo-800' : currentTask.Priority === 'Medium' ? 'bg-sky-100 text-sky-800' : 'bg-emerald-100 text-emerald-800'}`}>
              <span>⚡</span>
              <span>{currentTask.Priority}</span>
            </div>
          </div>
        </div>
        
        {/* Show break times if it's a break task */}
        {currentTask.Break === 1 ? (
          <div className="mt-3 flex flex-row lg:flex-col items-center lg:items-start gap-3">
            <div className="w-full">
              {currentTask.BreakStart && currentTask.BreakEnd ? (
                <p className="w-full text-left text-xs text-indigo-700 font-semibold bg-indigo-50 px-3 py-1 rounded-full">
                  ⏱️ Break {formatTimeShort(currentTask.BreakStart)} - {formatTimeShort(currentTask.BreakEnd)}
                </p>
              ) : currentTask.BreakStart ? (
                <p className="w-full text-left text-xs text-indigo-700 font-medium bg-indigo-50 px-3 py-1 rounded-full">
                  ⏱️ On break since {formatTimeShort(currentTask.BreakStart)}
                </p>
              ) : (
                <p className="w-full text-left text-sm text-indigo-700 font-medium">⏱️ Break task</p>
              )}
            </div>

            <div className="w-full flex justify-end lg:justify-start">
              <IndividualActions />
            </div>
          </div>
        ) : (
          <p className="text-xs text-indigo-700  font-medium">
            {currentTask.StartTime} - {addHours(parse(currentTask.StartTime, "HH:mm:ss", new Date()), 1).toLocaleTimeString()}
          </p>
        )}

        {/* 🔥 Priority Progress Bar */}
        {currentTask.Break === 0 && (
        <div className="flex items-center space-x-2 mt-2">
          <div className="w-full bg-gray-200 rounded-md h-2 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                currentTask.Priority === "High" ? "w-full bg-indigo-500" : 
                currentTask.Priority === "Medium" ? "w-2/3 bg-sky-500" : 
                "w-1/3 bg-emerald-500"
              }`}
            ></div>

          </div>
          <p className="text-xs font-bold text-indigo-700">{currentTask.Priority}</p>
        </div>
        )}
    
  
  </div>
)}


<div className="flex items-center justify-between mb-4">
  <WorkCoachToday />
</div>



      {/* Combined Feed */}
      <ul role="list" className={`${mobileView ? 'space-y-3' : 'space-y-6'}`}>
        {combinedFeed.map((item, idx) => {
          const timeAgo = item.timestamp
            ? formatDistanceToNow(parseISO(item.timestamp), { addSuffix: true })
            : "Invalid time";

          return (
            <li key={idx} className={`relative flex gap-x-4 ${mobileView ? 'text-sm' : ''}`}>
              {/* Vertical line */}
              <div className="absolute left-0 top-0 w-6 flex justify-center">
                {idx < combinedFeed.length - 1 && (
                  <div className="w-px bg-gray-200" />
                )}
              </div>

              {/* Content */}
              <div className="flex-auto">
  {item.type === "post" && (
    <div className={`relative space-y-1 ${mobileView ? 'p-3 text-sm' : 'p-4'} rounded-2xl border border-white/35 bg-gradient-to-br from-sky-500/35 via-indigo-500/30 to-emerald-500/30 backdrop-blur-xl shadow-[0_14px_30px_rgba(15,23,42,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]`} >
      <h3 className="text-sm font-semibold text-indigo-900">News Feed</h3>

      {/* Icon Buttons - Top Right */}
      <div className="absolute top-2 right-2 flex space-x-2">
              {/* get the likes for the post to see if its been liked */}


        <button
          className="p-1 rounded-full border border-indigo-400/70 bg-white/60 hover:bg-white/80 transition-colors"
          onClick={() => handleLike(item)}
        >
          <HandThumbUpIcon className="h-6 w-6 text-indigo-800" />
        </button>
       
      </div>

      {/* Post Content */}
      <p className="text-sm font-medium text-indigo-900">{item.Title}</p>
      <p className="text-sm text-indigo-800">{item.Content}</p>
      <p className="text-xs text-sky-800">By {item.StaffName}</p>
      <p className="text-xs text-sky-800">{timeAgo}</p>
    </div>
  )}

                {item.type === "like" && (
 <div className={`space-y-1 ${mobileView ? 'p-2 text-sm' : 'p-3'} rounded-2xl border border-white/35 bg-gradient-to-br from-emerald-500/35 via-teal-500/30 to-sky-500/30 backdrop-blur-xl shadow-[0_12px_28px_rgba(15,23,42,0.28),inset_0_1px_0_rgba(255,255,255,0.28)]`}>
                    <h3 className="text-sm font-semibold text-indigo-900">News Feed</h3>
                    <UserCircleIcon className="h-6 w-6 text-indigo-800" />
                    <p className="text-sm text-indigo-900 font-medium "> 
                      {item.StaffName} liked a post
                    </p>
                    <p className="text-xs text-sky-800">{timeAgo}</p>
                  </div>
                )}
                {item.type === "comment" && (
                  <div className="p-4 rounded-2xl border border-white/35 bg-gradient-to-br from-indigo-500/35 via-sky-500/30 to-emerald-500/30 backdrop-blur-xl shadow-[0_12px_28px_rgba(15,23,42,0.28),inset_0_1px_0_rgba(255,255,255,0.28)]">
                    <h3 className="text-sm font-semibold text-indigo-900">News Feed</h3>
                    <UserCircleIcon className="h-6 w-6 text-indigo-800" />
                    <p className="text-sm font-medium text-indigo-900">
                      {item.StaffName} commented:
                    </p>
                    <p className="text-sm text-indigo-800">{item.Content}</p>
                    <p className="text-xs text-sky-800">{timeAgo}</p>
                  </div>
                )}
                {item.type === "message" && (
                  <div className="p-4 rounded-2xl border border-white/35 bg-gradient-to-br from-emerald-100/75 via-teal-100/70 to-sky-100/65 backdrop-blur-xl shadow-[0_10px_24px_rgba(15,23,42,0.2),inset_0_1px_0_rgba(255,255,255,0.35)]">
                    <UserCircleIcon className="h-6 w-6 text-emerald-700" />
                    <button onClick={() => setReply(item)}
                      className="text-sm font-semibold text-emerald-700 underline"
                    >
                      Message from {item.from}
                    </button>
                    <p className="text-sm text-indigo-800">{item.content}</p>
                    <p className="text-xs text-sky-800">{timeAgo}</p>
                  </div>
                )}
                {item.type === "activity" && (
                  <div className={`${mobileView ? 'text-sm' : ''} p-4 rounded-2xl border border-white/35 bg-gradient-to-br from-indigo-100/75 via-sky-100/70 to-emerald-100/65 backdrop-blur-xl shadow-[0_10px_24px_rgba(15,23,42,0.2),inset_0_1px_0_rgba(255,255,255,0.35)]`}>
                    <ClipboardDocumentCheckIcon className="h-6 w-6 text-indigo-700" />
                    <p className="text-sm font-semibold text-indigo-800">
                      {item.Content}
                    </p>
                    <p className="text-xs text-sky-800">{item.StaffName}</p>
                    {item.Accepted ? (
                      <div className="flex items-center">
                        <p className="text-xs text-emerald-700 mr-1">Task Accepted</p>
                        <CheckBadgeIcon className="h-6 w-6 text-emerald-700" />
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <p className="text-xs text-sky-700 mr-1">Awaiting Answer</p>
                        <XCircleIcon className="h-6 w-6 text-sky-700" />
                      </div>
                    )}
                    <p className="text-xs text-sky-800">{timeAgo}</p>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
